import { Component, HostListener, computed, inject, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { AuthStore } from '../../core/auth/state/auth.store';
import { ProfileAvatarService } from '../../core/auth/services/profile-avatar.service';
import { QuotaStore } from '../../core/auth/state/quota.store';
import { ToastService } from '../../core/services/toast.service';
import { Rights } from '../../core/constants/rights.constants';
import { UIStore } from '../../core/services/ui.store';
import {
  ManualPaymentUpgradeModalComponent,
  ManualPaymentUpgradeResult,
} from '../../shared/components/manual-payment-upgrade-modal/manual-payment-upgrade-modal.component';

type CreatorDestinationId =
  | 'edit'
  | 'templates'
  | 'jivu-ai'
  | 'jivu-command'
  | 'projects'
  | 'me';

interface CreatorDestination {
  readonly id: CreatorDestinationId;
  readonly label: string;
  readonly route: readonly string[];
  readonly icon: string;
}

@Component({
  selector: 'app-creator-navigation',
  standalone: true,
  imports: [RouterLink, ManualPaymentUpgradeModalComponent],
  templateUrl: './creator-navigation.component.html',
  styleUrl: './creator-navigation.component.scss',
})
export class CreatorNavigationComponent {
  protected readonly authStore = inject(AuthStore);
  protected readonly profileAvatar = inject(ProfileAvatarService);
  protected readonly uiStore = inject(UIStore);
  protected readonly upgradePaymentOpen = signal(false);
  protected readonly voiceMenuOpen = signal(false);
  protected readonly canUseTalkMode = computed(() => {
    const user = this.authStore.user();
    return !!user && (user.isSuperAdmin || user.rights?.includes(Rights.JivuTalkShell_Access));
  });
  private readonly router = inject(Router);
  private readonly quotaStore = inject(QuotaStore);
  private readonly toast = inject(ToastService);

  protected readonly desktopDestinations: readonly CreatorDestination[] = [
    { id: 'edit', label: 'Edit', route: ['/edit'], icon: 'fa-scissors' },
    { id: 'templates', label: 'Studio', route: ['/templates'], icon: 'fa-layer-group' },
    { id: 'jivu-ai', label: 'Jivu AI', route: ['/jivu-ai'], icon: 'fa-wand-magic-sparkles' },
    { id: 'jivu-command', label: 'Jivu Command', route: ['/jivu-command'], icon: 'fa-face-smile' },
    { id: 'projects', label: 'Projects', route: ['/projects'], icon: 'fa-folder' },
    { id: 'me', label: 'Me', route: ['/me'], icon: 'fa-user' },
  ];

  protected readonly mobileDestinations: readonly CreatorDestination[] = [
    { id: 'edit', label: 'Edit', route: ['/edit'], icon: 'fa-scissors' },
    { id: 'templates', label: 'Studio', route: ['/templates'], icon: 'fa-layer-group' },
    { id: 'jivu-ai', label: 'Jivu AI', route: ['/jivu-ai'], icon: 'fa-wand-magic-sparkles' },
    { id: 'projects', label: 'Projects', route: ['/projects'], icon: 'fa-folder' },
    { id: 'jivu-command', label: 'Command', route: ['/jivu-command'], icon: 'fa-face-smile' },
    { id: 'me', label: 'Me', route: ['/me'], icon: 'fa-user' },
  ];


  constructor() {
    this.profileAvatar.ensureLoaded();
  }

  protected openUpgradePayment(): void {
    this.upgradePaymentOpen.set(true);
  }

  protected closeUpgradePayment(): void {
    this.upgradePaymentOpen.set(false);
  }

  protected onUpgradePaymentSubmitted(result: ManualPaymentUpgradeResult): void {
    if (result.status === 'activated') {
      this.authStore.refreshUser();
      this.quotaStore.refreshQuotas();
      this.toast.success(result.planName + ' plan activated.');
      return;
    }

    this.toast.success(result.planName + ' payment is pending admin approval.');
  }

  protected toggleJivuControl(event: MouseEvent): void {
    event.stopPropagation();
    if (this.uiStore.isGlobalMicActive()) {
      this.uiStore.closeGlobalMic();
      this.voiceMenuOpen.set(false);
      return;
    }

    this.voiceMenuOpen.update((open) => !open);
  }

  protected startJivuMode(mode: 'talk' | 'work', event: MouseEvent): void {
    event.stopPropagation();
    if (mode === 'talk' && !this.canUseTalkMode()) return;
    this.voiceMenuOpen.set(false);
    this.uiStore.openGlobalMic(mode);
  }

  protected keepVoiceMenuOpen(event: MouseEvent): void {
    event.stopPropagation();
  }

  @HostListener('document:click')
  protected closeVoiceMenu(): void {
    this.voiceMenuOpen.set(false);
  }

  protected isActive(destination: CreatorDestination): boolean {
    const path = this.router.url.split(/[?#]/)[0];

    switch (destination.id) {
      case 'edit':
        return path === '/edit' || path === '/quick-tools';
      case 'templates':
        return path === '/templates' || path.startsWith('/social/island');
      case 'jivu-ai':
        return path === '/jivu-ai' || path.startsWith('/video-gen-new') || path === '/memory';
      case 'jivu-command':
        return path.startsWith('/jivu-command');
      case 'projects':
        return (
          path.startsWith('/projects') ||
          path.startsWith('/review') ||
          (path.startsWith('/social') && !path.startsWith('/social/island'))
        );
      case 'me':
        return path.startsWith('/me') || path.startsWith('/settings') || path.startsWith('/upgrade');
    }
  }
}
